import React, { useEffect, useState } from 'react';
import {
  Table, Button, Space, Tag, Modal, Input, message, Card, Select, Badge, Form, Row, Col,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, ShopOutlined,
  PlusOutlined, EditOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { dealerService } from '../../api/services/dealerService';

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'gold',
  ACTIVE: 'green',
  APPROVED: 'green',
  REJECTED: 'red',
};

const SUB_COLOR = (has: boolean) => (has ? 'green' : 'default');

const fmt = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const useDealerFields = () => {
  const { t } = useTranslation();
  return [
    { name: 'companyName', label: t('admin.dealers.field_company_name'), required: true, span: 12 },
    { name: 'email', label: t('admin.dealers.field_business_email'), required: true, span: 12 },
    { name: 'phone', label: t('admin.dealers.field_phone'), required: true, span: 12 },
    { name: 'city', label: t('admin.dealers.field_city'), required: true, span: 12 },
    { name: 'street', label: t('admin.dealers.field_street'), required: true, span: 12 },
    { name: 'streetNumber', label: t('admin.dealers.field_street_number'), required: true, span: 12 },
    { name: 'zipCode', label: t('admin.dealers.field_zip_code'), required: true, span: 12 },
    { name: 'additionalAddress', label: t('admin.dealers.field_additional_address'), required: false, span: 12 },
    { name: 'country', label: t('admin.dealers.field_country'), required: false, span: 12 },
    { name: 'dealerEmail', label: t('admin.dealers.field_dealer_email'), required: false, span: 12 },
    { name: 'dealerPassword', label: t('admin.dealers.field_dealer_password'), required: false, span: 12, password: true },
  ];
};

const AdminDealers: React.FC = () => {
  const { t } = useTranslation();
  const DEALER_FIELDS = useDealerFields();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState('');

  // Reject state
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Create/Edit modal
  const [createOpen, setCreateOpen] = useState(false);
  const [editDealer, setEditDealer] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await dealerService.getAllDealers();
      setData(res);
      setFiltered(res);
    } catch {
      message.error(t('admin.dealers.msg_load_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  useEffect(() => {
    setFiltered(statusFilter ? data.filter(d => d.status === statusFilter) : data);
  }, [statusFilter, data]);

  const handleApprove = async (dealerId: string) => {
    try {
      await dealerService.approveDealer(dealerId);
      message.success(t('admin.dealers.msg_activated'));
      fetch();
    } catch {
      message.error(t('admin.dealers.msg_approve_failed'));
    }
  };

  const handleReject = async () => {
    if (!rejectingId || !rejectionReason.trim()) {
      message.warning(t('admin.dealers.msg_reason_required'));
      return;
    }
    try {
      await dealerService.rejectDealer(rejectingId, { rejectionReason });
      message.success(t('admin.dealers.msg_rejected'));
      setRejectingId(null);
      setRejectionReason('');
      fetch();
    } catch {
      message.error(t('admin.dealers.msg_reject_failed'));
    }
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setSaving(true);
      await dealerService.adminCreateDealer({ ...values, status: 'ACTIVE' });
      message.success(t('admin.dealers.msg_created'));
      setCreateOpen(false);
      createForm.resetFields();
      fetch();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || t('admin.dealers.msg_create_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editDealer) return;
    try {
      const values = await editForm.validateFields();
      const payload: any = {};
      Object.entries(values).forEach(([k, v]) => {
        if (v !== '' && v !== undefined && v !== null) payload[k] = v;
      });
      setSaving(true);
      await dealerService.adminUpdateDealer(editDealer.dealerId, payload);
      message.success(t('admin.dealers.msg_updated'));
      setEditDealer(null);
      editForm.resetFields();
      fetch();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || t('admin.dealers.msg_update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row: any) => {
    setEditDealer(row);
    editForm.setFieldsValue({
      companyName: row.companyName ?? '',
      email: row.email ?? '',
      phone: row.phone ?? '',
      city: row.city ?? '',
      street: row.street ?? '',
      streetNumber: row.streetNumber ?? '',
      zipCode: row.zipCode ?? '',
      additionalAddress: row.additionalAddress ?? '',
      country: row.country ?? '',
      dealerEmail: row.dealerEmail ?? '',
      adminNotes: row.adminNotes ?? '',
      status: row.status ?? 'ACTIVE',
      dealerPassword: '',
    });
  };

  const pendingCount = data.filter(d => d.status === 'PENDING').length;

  const columns = [
    {
      title: t('admin.dealers.col_company'),
      dataIndex: 'companyName',
      key: 'companyName',
      render: (v: string) => (
        <Space>
          <ShopOutlined style={{ color: '#fe5a1d' }} />
          <span style={{ fontWeight: 600 }}>{v}</span>
        </Space>
      ),
    },
    { title: t('admin.dealers.col_email'), dataIndex: 'email', key: 'email' },
    {
      title: t('admin.dealers.col_city'),
      dataIndex: 'city',
      key: 'city',
      render: (v: string) => v || '—',
    },
    {
      title: t('admin.dealers.col_status'),
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: t('admin.dealers.col_subscription'),
      key: 'sub',
      render: (_: any, row: any) => (
        row.hasActiveSub
          ? <Tag color={SUB_COLOR(true)}>{row.activeSubPackage ?? t('admin.dealers.sub_active')}</Tag>
          : <Tag color={SUB_COLOR(false)}>{t('admin.dealers.sub_none')}</Tag>
      ),
    },
    {
      title: t('admin.dealers.col_registered'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: fmt,
    },
    {
      title: t('admin.dealers.col_approved'),
      dataIndex: 'approvedAt',
      key: 'approvedAt',
      render: fmt,
    },
    {
      title: t('admin.dealers.col_actions'),
      key: 'actions',
      width: 200,
      render: (_: any, row: any) => (
        <Space size={6}>
          <Button
            icon={<EditOutlined />}
            size="small"
            style={{ borderRadius: 6 }}
            onClick={() => openEdit(row)}
          >
            {t('admin.dealers.btn_edit')}
          </Button>
          {row.status === 'PENDING' && (
            <>
              <Button
                icon={<CheckCircleOutlined />}
                className="admin-action-approve"
                size="small"
                onClick={() => handleApprove(row.dealerId)}
              >
                {t('admin.dealers.btn_approve')}
              </Button>
              <Button
                icon={<CloseCircleOutlined />}
                className="admin-action-reject"
                size="small"
                onClick={() => setRejectingId(row.dealerId)}
              >
                {t('admin.dealers.btn_reject')}
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const dealerFormFields = (
    <Row gutter={16}>
      {DEALER_FIELDS.map(f => (
        <Col span={f.span} key={f.name}>
          <Form.Item
            name={f.name}
            label={f.label}
            rules={f.required ? [{ required: true, message: `${f.label} ${t('admin.dealers.field_required_suffix')}` }] : []}
          >
            {(f as any).password
              ? <Input.Password placeholder={f.label} autoComplete="new-password" />
              : <Input placeholder={f.label} />
            }
          </Form.Item>
        </Col>
      ))}
    </Row>
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card
        title={
          <Space>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--admin-text-primary)' }}>
              {t('admin.dealers.title')}
            </span>
            {pendingCount > 0 && (
              <Badge count={pendingCount} style={{ background: '#fa8c16' }} />
            )}
          </Space>
        }
        extra={
          <Space>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 140, borderRadius: 8 }}
              options={[
                { label: t('admin.dealers.filter_all'), value: '' },
                { label: t('admin.dealers.filter_pending'), value: 'PENDING' },
                { label: t('admin.dealers.filter_active'), value: 'ACTIVE' },
                { label: t('admin.dealers.filter_rejected'), value: 'REJECTED' },
              ]}
            />
            <Button
              icon={<PlusOutlined />}
              type="primary"
              style={{ borderRadius: 8, background: '#fe5a1d', borderColor: '#fe5a1d' }}
              onClick={() => { createForm.resetFields(); setCreateOpen(true); }}
            >
              {t('admin.dealers.btn_create')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetch} loading={loading} style={{ borderRadius: 8 }}>
              {t('admin.dealers.btn_refresh')}
            </Button>
          </Space>
        }
        className="admin-table-card"
        bordered={false}
      >
        <Table
          rowKey={r => r.dealerId}
          loading={loading}
          columns={columns as any}
          dataSource={filtered}
          pagination={{ pageSize: 15 }}
          className="admin-table"
          scroll={{ x: true }}
        />
      </Card>

      {/* Reject Modal */}
      <Modal
        title={t('admin.dealers.modal_reject_title')}
        open={!!rejectingId}
        okText={t('admin.dealers.modal_reject_ok')}
        okButtonProps={{ danger: true, style: { borderRadius: 8, fontWeight: 600 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        onOk={handleReject}
        onCancel={() => { setRejectingId(null); setRejectionReason(''); }}
      >
        <div style={{ fontSize: 13, color: 'var(--admin-text-secondary)', marginBottom: 12 }}>
          {t('admin.dealers.modal_reject_desc')}
        </div>
        <Input.TextArea
          rows={4}
          placeholder={t('admin.dealers.modal_reject_placeholder')}
          value={rejectionReason}
          onChange={e => setRejectionReason(e.target.value)}
        />
      </Modal>

      {/* Create Dealer Modal */}
      <Modal
        title={t('admin.dealers.modal_create_title')}
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        okText={t('admin.dealers.modal_create_ok')}
        confirmLoading={saving}
        width={700}
        okButtonProps={{ style: { background: '#fe5a1d', borderColor: '#fe5a1d', borderRadius: 8, fontWeight: 600 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          {dealerFormFields}
        </Form>
      </Modal>

      {/* Edit Dealer Modal */}
      <Modal
        title={t('admin.dealers.modal_edit_title', { name: editDealer?.companyName ?? '' })}
        open={!!editDealer}
        onOk={handleEdit}
        onCancel={() => { setEditDealer(null); editForm.resetFields(); }}
        okText={t('admin.dealers.modal_edit_ok')}
        confirmLoading={saving}
        width={700}
        okButtonProps={{ style: { background: '#fe5a1d', borderColor: '#fe5a1d', borderRadius: 8, fontWeight: 600 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            {DEALER_FIELDS.map(f => (
              <Col span={f.span} key={f.name}>
                <Form.Item name={f.name} label={f.label}>
                  {(f as any).password
                    ? <Input.Password placeholder={t('admin.dealers.placeholder_keep_current')} autoComplete="new-password" />
                    : <Input placeholder={f.label} />
                  }
                </Form.Item>
              </Col>
            ))}
            <Col span={12}>
              <Form.Item name="status" label={t('admin.dealers.label_status')}>
                <Select
                  options={[
                    { label: t('admin.dealers.status_active'), value: 'ACTIVE' },
                    { label: t('admin.dealers.status_pending'), value: 'PENDING' },
                    { label: t('admin.dealers.status_rejected'), value: 'REJECTED' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="adminNotes" label={t('admin.dealers.label_admin_notes')}>
                <Input.TextArea rows={3} placeholder={t('admin.dealers.placeholder_admin_notes')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Space>
  );
};

export default AdminDealers;
